import { FirebaseApp, FirebaseOptions, getApps, initializeApp } from 'firebase/app';
import { Auth, getAuth, signInWithCustomToken } from 'firebase/auth';
import { Firestore, doc, getFirestore, initializeFirestore, updateDoc } from 'firebase/firestore';

let _chatFirestore: Firestore | null = null;
let _chatAuth: Auth | null = null;
let _initPromise: Promise<void> | null = null;

export interface ChatFirebaseOptions {
    /** Firebase project config */
    firebaseConfig: FirebaseOptions;
    /** Named Firestore database ID (e.g. 'firestore-identity-test') */
    databaseId: string;
    /**
     * Firebase custom token to sign in with.
     * Only needed when no existing Firebase Auth session is present in the app.
     * If omitted, the SDK reuses the existing auth session (if any).
     */
    customToken?: string;
}

async function doInit({ firebaseConfig, databaseId, customToken }: ChatFirebaseOptions): Promise<void> {
    // Case 1: reuse existing app with same appId → preserves existing Firebase Auth session
    const existingApp: FirebaseApp | undefined = getApps().find((a) => a.options.appId === firebaseConfig.appId);
    const app: FirebaseApp = existingApp ?? initializeApp(firebaseConfig, 'chat-firebase');

    // Always use initializeFirestore with the named databaseId.
    // getFirestore(app) would return the DEFAULT database, not the named one.
    // If initializeFirestore was already called for this app+databaseId (e.g. HMR), it throws,
    // so we fall back via a runtime cast since @types only declares 0-1 args for getFirestore.
    try {
        _chatFirestore = initializeFirestore(app, {}, databaseId);
    } catch {
        _chatFirestore = (getFirestore as unknown as (app: FirebaseApp, databaseId: string) => Firestore)(
            app,
            databaseId,
        );
    }

    _chatAuth = getAuth(app);

    // Case 2: no current user + custom token provided → sign in with custom token
    if (!_chatAuth.currentUser && customToken) {
        await signInWithCustomToken(_chatAuth, customToken);
    }
}

/**
 * Initializes the shared Firebase instances used by the chat feature.
 * Safe to call multiple times — only the first call has effect (idempotent).
 */
export function initChatFirebase(options: ChatFirebaseOptions): Promise<void> {
    if (!_initPromise) {
        _initPromise = doInit(options);
    }
    return _initPromise;
}

/**
 * Returns the Firestore instance.
 * Throws if `initChatFirebase` has not been called yet.
 */
export function getChatFirestore(): Firestore {
    if (!_chatFirestore) {
        throw new Error('[chat] Firestore not initialized. Call initChatFirebase() before using the chat.');
    }
    return _chatFirestore;
}

/**
 * Sets unreadCount to 0 on `driver-app/{driverId}`.
 * @deprecated Only kept for apps/chat-driver-saas (unrelated, discarded WIP app). stork-app's
 * driver chat uses the Mongo-backed `lastReadBy` watermark instead (see chat-participants markAsRead).
 */
export async function markConversationAsRead(driverId: string): Promise<void> {
    const firestore = getChatFirestore();
    try {
        await updateDoc(doc(firestore, `driver-app/${driverId}`), { unreadCount: 0 });
    } catch {
        // Document doesn't exist yet — unreadCount is already 0, nothing to reset
    }
}

const GET_CHAT_AUTH_RETRY_DELAYS_MS = [200, 400, 800, 1600];

/**
 * Returns the Auth instance (e.g. to read the signed-in user's uid), retrying with backoff
 * a few times in case `initChatFirebase` is still resolving. Resolves to null (never throws)
 * if it's still not ready after retrying — the caller decides how to handle that gracefully.
 */
export async function getChatAuth(): Promise<Auth | null> {
    if (_chatAuth) return _chatAuth;

    for (const delayMs of GET_CHAT_AUTH_RETRY_DELAYS_MS) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
        if (_chatAuth) return _chatAuth;
    }

    return null;
}
